import { DateTime } from 'luxon';
import { Measurements, getBinLevelMeasurementOverrides } from '~/utility/BinSentry-ui-utility';
import { css, html, LitEntity, renderIf, nothing } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import '../../common/bn-banner';
import '../../common/bn-button';
import '../../common/bn-surface';
import '../../common/bn-toggle';
import './bin-height-map';
import './bin-calendar-mark-manual-feed-mask';
import './bin-level-amplitude-image';

class BinHeightMapInspector extends LitEntity {
  constructor() {
    super();
    this.forecast = false;
  }

  static get properties() {
    return {
      binLevelId: { type: String, attribute: 'bin-level-id' },
      renderer: { type: Object, attribute: false },
      measurement: { type: Object, attribute: false },
      binLevelHTML: { type: Object, attribute: false },
      binLevelDate: { type: String, attribute: false },
      imageToolsUrl: { type: String, attribute: false },
      forecast: { type: Boolean, attribute: false },
      role: { type: String, attribute: false },
      advancedMode: { type: Boolean, attribute: 'advanced-mode', reflect: true },
      showAmplitudeImage: { type: Boolean, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }
      .head {
        display: flex;
        gap: 1em;
        align-items: baseline;
      }
      .body {
        flex-grow: 1;
        width: 100%;
        display: flex;
        gap: 1em;
        align-items: stretch;
        overflow: hidden;
      }
      .status {
        flex-grow: 1;
        display: flex;
        justify-content: center;
        padding: 2rem;
      }
      bin-height-map {
        flex-grow: 1;
        width: 1px;
        align-self: stretch;
      }
      .control-box {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        width: 320px;
        overflow: auto;
      }
      .amplitude-image {
        border: 1px solid var(--gray-40);
        background: var(--gray-20);
      }
      iframe {
        width: 100%;
        height: 100%;
        border: 0;
      }

      bn-surface {
        flex-grow: 1;
        align-self: stretch;
        margin: 0;
        display: flex;
      }

      @media(max-width: 620px) {
        .control-box {
          display: none;
        }
      }
    `];
  }

  renderEntity() {
    return html`
      <div class="head">
        ${this._renderLevelInfo()}
      </div>

      <div class="body">
          ${this._renderBody()}
      </div>

      <div class="foot">
        ${this._renderLevels()}
      </div>
    `;
  }

  _renderLevelInfo() {
    return html`
      <h3>${this.binLevelDate || nothing}</h3>
      <h3>${this.binLevelHTML || nothing}</h3>
      ${renderIf(this.imageToolsUrl, () => html`
        <bn-button variant="text" @click="${this._toggleMode}">
          ${this.advancedMode ? 'Basic' : 'Advanced'} Tools
        </bn-button>
        <a href="/readings?binLevelId=${encodeURIComponent(this.binLevelId)}">
          <bn-button variant="text">Reading Inspector</bn-button>
        </a>
      `)}
    `;
  }

  _renderBody() {
    if (this.forecast) {
      return html`
        <div class="status">
          <bn-banner severity="info">
            Height maps are not generated for forecasted bin levels.
          </bn-banner>
        </div>
      `;
    }

    if (this.advancedMode) {
      const toolsUrl = new URL(`https://${window.location.host}/3d-image-tools`);
      toolsUrl.hash = `/binLevelId/${encodeURIComponent(this.binLevelId)}`;
      return html`<iframe src="${toolsUrl.href}">&nbsp</iframe>`;
    }

    return html`
      <bn-surface>
        <bin-height-map
          .href="${this.href}"
          user-control
          bin-level-id="${this.binLevelId || ''}"
          @renderer-mounted="${(e) => this.renderer = e.detail.renderer}"
          @loaded-height-map="${this._onLoadedHeightMap}"
          ?hide-legs="${this.role === 'ingredient'}"
        >
          <p class="subdued">
            No height map information at the time selected.
          </p>
        </bin-height-map>
      </bn-surface>

      ${this._renderControls()}
    `;
  }

  _renderControls() {
    const showImage = this.showAmplitudeImage && !this.advancedMode && this.role === 'ingredient';
    const disabled = !this.renderer;
    return html`
      <div class="control-box">
        <h3>Controls</h3>

        <div class="controls">
          <div class="form-field">
            <label>
              Cross Section
              <input type="range" value="100" @input="${this._onChangeRange}" />
            </label>
          </div>

          <bn-toggle
            name="Auto Rotate"
            ?disabled="${disabled}"
            ?checked="${this.renderer?.autoRotate}"
            @update="${({ detail: { value } }) => this.renderer.autoRotate = value}"
          ></bn-toggle>

          <bn-toggle
            name="Show Bin"
            ?disabled="${disabled}"
            ?checked="${!this.renderer?.hideBin}"
            @update="${this._onToggleShowBin}"
          ></bn-toggle>

          <bn-toggle
            name="Show Rings"
            ?disabled="${disabled || this.renderer.hideBin}"
            ?checked="${!this.renderer?.hideRings}"
            @update="${({ detail: { value } }) => this.renderer.hideRings = !value}"
          ></bn-toggle>

          <bn-toggle
            name="Show Legs"
            ?disabled="${disabled || this.renderer.hideBin}"
            ?checked="${!this.renderer?.hideLegs}"
            @update="${({ detail: { value } }) => this.renderer.hideLegs = !value}"
          ></bn-toggle>

          <bn-toggle
            name="Solid Walls"
            ?disabled="${disabled || this.renderer.hideBin}"
            ?checked="${this.renderer?.solidBinWalls}"
            @update="${({ detail: { value } }) => this.renderer.solidBinWalls = value}"
          ></bn-toggle>

          ${renderIf(this.role === 'ingredient', () => html`
            <bn-toggle
              name="Show Amplitude Image"
              ?checked="${this.showAmplitudeImage}"
              @update="${({ detail: { value } }) => this.showAmplitudeImage = value}"
            ></bn-toggle>
          `)}
        </div>

        ${renderIf(showImage, () => html`
          <div class="amplitude-image">
            <bin-level-amplitude-image .href=${this.binLevelId}></bin-level-amplitude-image>
          </div>
        `)}
      </div>
    `;
  }

  _renderLevels() {
    if (!this.entity) {
      return nothing;
    }

    const { role } = this.entity.properties;
    let range;
    if (role === 'ingredient') {
      const endTime = DateTime.now().startOf('hour').plus({ hours : 2 }).toUTC();
      range = {
        start: endTime.minus({ days: 1 }).toISO(),
        end: endTime.toISO(),
      };
    } else {
      range = {
        start: DateTime.now().startOf('day').toUTC().minus({ days: 14 }).toISO(),
        end: DateTime.now().endOf('day').plus({ days: 5 }).toUTC().toISO(),
      };
    }

    const contentLabel = role === 'ingredient' ? 'inventory' : 'feed';

    const selected = ({ detail: { point } }) => {
      if (!point) {
        return;
      }
      const { id: binLevelId, publishedAt, volume, weight, bulkDensity } = point;
      this.binLevelId = binLevelId;
      this.binLevelHTML = this.measurement?.html({
        volume,
        weight,
        bulkDensity,
        totalVolume: this.getProperty('volumes.fullTotal'),
      });
      this.binLevelPublishedAt = new Date(publishedAt).toISOString();
      this.binLevelDate = DateTime.fromISO(publishedAt).toLocaleString(DateTime.DATETIME_MED);
    };

    return html`
      <bin-level-smooth-history-graph
        href=${this.href}
        start=${range.start}
        end=${range.end}
        content-label=${contentLabel}
        highlight-selection
        highlight-manual-feed-mask
        @bn:select-data-point=${selected}
        show-raw
        hide-options
        hide-legend
        heightOverride="120px"
        small
      ></bin-level-smooth-history-graph>
    `;
  }

  async _entityChanged(entity) {
    super._entityChanged(entity);
    this.measurement = Measurements.get('binLevel', await getBinLevelMeasurementOverrides(this.entity));
    this.role = this.entity.properties.role;
  }

  _onToggleShowBin({ detail: { value } }) {
    this.renderer.hideBin = !value;
    this.requestUpdate();
  }

  _onChangeRange(e) {
    this.renderer.clipPosition = -1.5 + (3 * (e.target.value / 100));
  }

  _onLoadedHeightMap({ detail: { heightMap, binLevel } }) {
    const threeDImage = heightMap.getSubEntity('https://api.binsentry.com/rel/3d-image-data');
    if (threeDImage) {
      this.binLevelId = binLevel.getLink('self').href;
      const imageToolsUrl = new URL(`${window.location.origin}/3d-image-tools`);
      imageToolsUrl.searchParams.append('binLevelId', this.binLevelId);
      this.imageToolsUrl = imageToolsUrl.href;
    }
    this._updateFieldsFromLevel(binLevel);
  }

  _toggleMode() {
    this.advancedMode = !this.advancedMode;
  }

  _updateFieldsFromLevel(binLevel) {
    if (binLevel) {
      this.binLevelId = this.getHref('self', binLevel);
      this.binLevelHTML = this.measurement?.html({
        volume: binLevel.properties.volume,
        weight: binLevel.properties.weight,
        bulkDensity: this.getProperty('bulkDensity'),
        totalVolume: this.getProperty('volumes.fullTotal'),
      });
      this.binLevelPublishedAt = new Date(binLevel.properties.publishedAt).toISOString();
      this.binLevelDate = DateTime
        .fromISO(binLevel.properties.publishedAt)
        .toLocaleString(DateTime.DATETIME_MED);
    }
  }
}

window.customElements.define('bin-height-map-inspector', BinHeightMapInspector);
